import { forwardRef, useState } from 'react'
import { clsx } from 'clsx'
import { useResponsive } from '@/contexts/ResponsiveContext'
import { EyeIcon, EyeSlashIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline'

/**
 * Enhanced Input component with responsive sizing and validation
 */
const Input = forwardRef(({
  label,
  type = 'text',
  placeholder,
  value,
  onChange,
  onBlur,
  onFocus,
  error,
  helpText,
  required = false,
  disabled = false,
  fullWidth = true,
  size = 'md',
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  showPasswordToggle = false,
  className = '',
  labelClassName = '',
  inputClassName = '',
  id,
  name,
  autoComplete,
  ...props
}, ref) => {
  const { isMobile } = useResponsive()
  const [showPassword, setShowPassword] = useState(false)
  const [focused, setFocused] = useState(false)

  // Generate unique ID if not provided
  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`

  // Determine actual input type
  const actualType = type === 'password' && showPassword ? 'text' : type

  // Base input classes
  const baseInputClasses = 'block border rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation'

  // Size classes - responsive
  const getSizeClasses = () => {
    if (isMobile) {
      // Mobile-optimized sizes (touch-friendly)
      const mobileSizes = {
        sm: 'h-11 px-4 text-base',
        md: 'h-12 px-4 text-base',
        lg: 'h-14 px-6 text-lg'
      }
      return mobileSizes[size] || mobileSizes.md
    } else {
      // Desktop sizes
      const desktopSizes = {
        sm: 'h-9 px-3 text-sm',
        md: 'h-10 px-3 text-sm',
        lg: 'h-11 px-4 text-base'
      }
      return desktopSizes[size] || desktopSizes.md
    }
  }

  // State-based classes
  const getStateClasses = () => {
    if (error) {
      return 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500'
    }
    if (focused) {
      return 'border-primary-300 focus:ring-primary-500 focus:border-primary-500'
    }
    return 'border-gray-300 focus:ring-primary-500 focus:border-primary-500'
  }

  // Icon padding classes
  const getIconPaddingClasses = () => {
    const hasLeftIcon = LeftIcon
    const hasRightIcon = RightIcon || (type === 'password' && showPasswordToggle) || error
    
    let classes = ''
    if (hasLeftIcon) classes += isMobile ? ' pl-12' : ' pl-10'
    if (hasRightIcon) classes += isMobile ? ' pr-12' : ' pr-10'
    
    return classes
  }

  // Combine input classes
  const inputClasses = clsx(
    baseInputClasses,
    getSizeClasses(),
    getStateClasses(),
    getIconPaddingClasses(),
    fullWidth ? 'w-full' : '',
    inputClassName
  )

  // Label classes
  const labelClasses = clsx(
    'block text-sm font-medium mb-1',
    error ? 'text-red-700' : 'text-gray-700',
    disabled ? 'opacity-50' : '',
    labelClassName
  )

  // Handle focus events
  const handleFocus = (event) => {
    setFocused(true)
    onFocus?.(event)
  }

  const handleBlur = (event) => {
    setFocused(false)
    onBlur?.(event)
  }

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev)
  }

  return (
    <div className={clsx('relative', fullWidth ? 'w-full' : '', className)}>
      {/* Label */}
      {label && (
        <label htmlFor={inputId} className={labelClasses}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Input container */}
      <div className="relative">
        {/* Left icon */}
        {LeftIcon && (
          <div className={clsx(
            'absolute inset-y-0 left-0 flex items-center pointer-events-none',
            isMobile ? 'pl-4' : 'pl-3'
          )}>
            <LeftIcon 
              className={clsx(
                'text-gray-400',
                isMobile ? 'h-5 w-5' : 'h-4 w-4'
              )} 
            />
          </div>
        )}

        {/* Input field */}
        <input
          ref={ref}
          id={inputId}
          name={name}
          type={actualType}
          value={value}
          onChange={onChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={autoComplete}
          className={inputClasses}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={
            error ? `${inputId}-error` : 
            helpText ? `${inputId}-help` : undefined
          }
          {...props}
        />

        {/* Right icons */}
        <div className={clsx(
          'absolute inset-y-0 right-0 flex items-center',
          isMobile ? 'pr-4' : 'pr-3'
        )}>
          {/* Error icon */}
          {error && (
            <ExclamationCircleIcon 
              className={clsx(
                'text-red-500',
                isMobile ? 'h-5 w-5' : 'h-4 w-4',
                (type === 'password' && showPasswordToggle) || RightIcon ? 'mr-2' : ''
              )} 
            />
          )}

          {/* Password toggle */}
          {type === 'password' && showPasswordToggle && (
            <button
              type="button"
              onClick={togglePasswordVisibility}
              className={clsx(
                'text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600 transition-colors touch-manipulation',
                RightIcon ? 'mr-2' : ''
              )}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <EyeSlashIcon className={isMobile ? 'h-5 w-5' : 'h-4 w-4'} />
              ) : (
                <EyeIcon className={isMobile ? 'h-5 w-5' : 'h-4 w-4'} />
              )}
            </button>
          )}

          {/* Right icon */}
          {RightIcon && (
            <RightIcon 
              className={clsx(
                'text-gray-400',
                isMobile ? 'h-5 w-5' : 'h-4 w-4'
              )} 
            />
          )}
        </div>
      </div>

      {/* Error message */}
      {error && (
        <p 
          id={`${inputId}-error`}
          className="mt-1 text-sm text-red-600"
          role="alert"
        >
          {error}
        </p>
      )}

      {/* Help text */}
      {helpText && !error && (
        <p 
          id={`${inputId}-help`}
          className="mt-1 text-sm text-gray-500"
        >
          {helpText}
        </p>
      )}
    </div>
  )
})

Input.displayName = 'Input'

export default Input